import React, { useState, useMemo, useEffect } from 'react';
import { Terminal, X, Target, Shield, Settings } from 'lucide-react';
import { deviceMatchesOS } from '../utils/devicePlatformMatch';
import DevicePicker, { getDeviceId } from './DevicePicker';

/**
 * Modal to configure and confirm deployment of a baseline script to targets.
 * Used by BuntuDashboard and StandardPoliciesView.
 */
const DeploymentModal = ({ isOpen, onClose, script, preSelectedTarget, onConfirm, deviceGroups, devices = [] }) => {
  const [selectedTargetId, setSelectedTargetId] = useState('');
  const [selectedDeviceIds, setSelectedDeviceIds] = useState([]);
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [targetType, setTargetType] = useState('single');
  const [paramValues, setParamValues] = useState({});
  const [forceSelectionMode, setForceSelectionMode] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedTargetId('');
    setSelectedDeviceIds([]);
    setSelectedFolderId(null);
    setTargetType('single');
    setParamValues({});
    setForceSelectionMode(false);
  }, [isOpen, script?.id]);

  const deviceList = Array.isArray(devices) ? devices : [];

  const scriptDeviceType = script?.deviceType;
  const platformMap = {
    windows: 'plat-windows',
    android: 'plat-android',
    macos: 'plat-macos',
    linux: 'plat-linux',
    ios: 'plat-ios',
  };
  const requiredPlatform = scriptDeviceType ? platformMap[scriptDeviceType] : null;

  const compatibleDevices = useMemo(() => {
    if (!requiredPlatform && !scriptDeviceType) return deviceList;
    return deviceList.filter(
      (d) =>
        deviceMatchesOS(d, requiredPlatform || '') ||
        String(d.platform || '').toLowerCase() === String(scriptDeviceType || '').toLowerCase() ||
        String(d.os || '').toLowerCase().includes(String(scriptDeviceType || '').toLowerCase())
    );
  }, [deviceList, requiredPlatform, scriptDeviceType]);

  if (!isOpen || !script) return null;

  const deptCategory = deviceGroups?.[0]?.children?.find((c) => c.id === 'cat-depts');
  const platformCategory = deviceGroups?.[0]?.children?.find((c) => c.id === 'cat-platforms');
  const departments = deptCategory?.children || [];
  const platforms = platformCategory?.children || [];

  const params = script.params || [];
  const hasParams = params.length > 0;
  const isTargetUnknown = !preSelectedTarget || forceSelectionMode;

  const handleParamChange = (name, value) => {
    setParamValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleConfirm = () => {
    let finalTargetName = '';
    let targetId = selectedTargetId;

    if (!isTargetUnknown) {
      finalTargetName = preSelectedTarget.name;
      targetId = preSelectedTarget.id || selectedTargetId;
    } else if (targetType === 'collection') {
      const named =
        departments.find((d) => d.id === selectedTargetId)?.name ||
        platforms.find((p) => p.id === selectedTargetId)?.name ||
        selectedTargetId;
      finalTargetName = `Collection: ${named}`;
    } else if (targetType === 'single') {
      const d = compatibleDevices.find((x) => getDeviceId(x) === selectedTargetId);
      finalTargetName = d ? `Device: ${d.name || selectedTargetId}` : `System ID: ${selectedTargetId}`;
    } else if (targetType === 'multiple') {
      targetId = selectedDeviceIds.join(',');
      finalTargetName = `Multiple Systems: ${selectedDeviceIds.length} device(s)`;
    }

    let paramDesc = '';
    if (hasParams) {
      const entries = Object.entries(paramValues);
      if (entries.length > 0) {
        paramDesc = ' [Params: ' + entries.map(([k, v]) => `${k}=${v}`).join(', ') + ']';
      }
    }

    onConfirm({
      targetType,
      targetId,
      targetIds: targetType === 'multiple' ? selectedDeviceIds : targetType === 'single' ? [selectedTargetId] : undefined,
      params: paramValues,
      description: finalTargetName + paramDesc,
    });
  };

  const canSubmit =
    (!isTargetUnknown ||
      (targetType === 'single' && !!selectedTargetId) ||
      (targetType === 'multiple' && selectedDeviceIds.length > 0) ||
      (targetType === 'collection' && !!selectedTargetId)) &&
    (!hasParams || params.every((p) => !p.required || paramValues[p.name]));

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in duration-200 max-h-[90vh] flex flex-col">
        <div className="bg-slate-50 border-b border-slate-200 p-4 flex justify-between items-center shrink-0">
          <h3 className="font-semibold text-slate-800 flex items-center gap-2">
            <Terminal size={18} className="text-slate-500" />
            {hasParams ? 'Configure & Deploy' : 'Deploy Baseline Script'}
          </h3>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto">
          <div className="flex items-start gap-4 mb-6">
            <div className={`p-3 rounded-lg bg-slate-100 ${script.color || ''}`}>
              {script.icon ? <script.icon size={24} /> : <Terminal size={24} />}
            </div>
            <div className="flex-1">
              <h4 className="text-lg font-bold text-slate-800">{script.title}</h4>
              <p className="text-sm text-slate-500 mt-1">{script.description}</p>
              {scriptDeviceType && (
                <div className="mt-2 inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                  <Shield size={12} />
                  {scriptDeviceType.toUpperCase()} Only Policy
                </div>
              )}
            </div>
          </div>

          <div className="bg-blue-50 rounded-lg p-4 border border-blue-100 mb-6">
            <div className="flex justify-between items-center mb-3">
              <h5 className="text-xs font-bold text-blue-600 uppercase tracking-wide flex items-center gap-2">
                <Target size={12} /> Target Scope
              </h5>
              {!isTargetUnknown && (
                <button
                  type="button"
                  onClick={() => setForceSelectionMode(true)}
                  className="text-[10px] text-blue-600 hover:text-blue-800 underline"
                >
                  Change Target
                </button>
              )}
            </div>

            {!isTargetUnknown ? (
              <>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-slate-500">Target Context:</span>
                  <span className="font-semibold text-slate-800">{preSelectedTarget.name}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Count:</span>
                  <span className="font-mono bg-blue-200 text-blue-800 px-2 py-0.5 rounded text-xs">
                    {preSelectedTarget.count || 'Dynamic'} Devices
                  </span>
                </div>
              </>
            ) : (
              <div className="space-y-3">
                <div className="flex gap-2 mb-2">
                  <button
                    type="button"
                    onClick={() => { setTargetType('single'); setSelectedTargetId(''); setSelectedDeviceIds([]); }}
                    className={`flex-1 text-xs py-1.5 rounded border ${targetType === 'single' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 border-slate-300'}`}
                  >
                    Single
                  </button>
                  <button
                    type="button"
                    onClick={() => { setTargetType('multiple'); setSelectedTargetId(''); setSelectedDeviceIds([]); }}
                    className={`flex-1 text-xs py-1.5 rounded border ${targetType === 'multiple' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 border-slate-300'}`}
                  >
                    Multiple
                  </button>
                  <button
                    type="button"
                    onClick={() => { setTargetType('collection'); setSelectedTargetId(''); setSelectedDeviceIds([]); }}
                    className={`flex-1 text-xs py-1.5 rounded border ${targetType === 'collection' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 border-slate-300'}`}
                  >
                    Collection
                  </button>
                </div>

                {targetType === 'collection' && (
                  <>
                    <div className="mb-3">
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Select Folder</label>
                      <select
                        className="w-full text-sm border-slate-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500"
                        onChange={(e) => {
                          setSelectedFolderId(e.target.value || null);
                          setSelectedTargetId('');
                        }}
                        value={selectedFolderId || ''}
                      >
                        <option value="">-- Select Folder (Optional) --</option>
                        {departments
                          .filter((d) => d.type === 'folder')
                          .map((folder) => (
                            <option key={folder.id} value={folder.id}>
                              {folder.name} ({folder.children?.length || 0} groups)
                            </option>
                          ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Select Group</label>
                      <select
                        className="w-full text-sm border-slate-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500"
                        onChange={(e) => setSelectedTargetId(e.target.value)}
                        value={selectedTargetId}
                      >
                        <option value="">-- Select Target Group --</option>
                        {selectedFolderId &&
                          (() => {
                            const selectedFolder = departments.find((d) => d.id === selectedFolderId);
                            const folderCollections = selectedFolder?.children || [];
                            if (folderCollections.length > 0) {
                              return (
                                <optgroup label={`Groups in "${selectedFolder?.name}"`}>
                                  {folderCollections.map((collection) => (
                                    <option key={collection.id} value={collection.id}>
                                      {collection.name}
                                    </option>
                                  ))}
                                </optgroup>
                              );
                            }
                            return null;
                          })()}
                        {departments.filter((d) => d.type === 'dept' && !d.folderId).length > 0 && (
                          <optgroup label="Root Level Groups">
                            {departments
                              .filter((d) => d.type === 'dept' && !d.folderId)
                              .map((dept) => (
                                <option key={dept.id} value={dept.id}>
                                  {dept.name} {dept.id !== 'Unassigned' && '(Group)'}
                                </option>
                              ))}
                          </optgroup>
                        )}
                        {platforms.length > 0 && (
                          <optgroup label="By Platform">
                            {platforms.map((plat) => {
                              if (requiredPlatform && plat.id !== requiredPlatform && plat.id !== scriptDeviceType) {
                                return null;
                              }
                              return (
                                <option key={plat.id} value={plat.id}>
                                  {plat.name} (Platform)
                                </option>
                              );
                            })}
                          </optgroup>
                        )}
                      </select>
                    </div>
                    {requiredPlatform && (
                      <p className="text-xs text-blue-600 mt-2 flex items-center gap-1">
                        <Shield size={12} /> Only {scriptDeviceType} devices will be targeted from the selected group.
                      </p>
                    )}
                  </>
                )}

                {targetType === 'single' && (
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Select Device</label>
                    <DevicePicker
                      devices={compatibleDevices}
                      value={selectedTargetId}
                      onChange={setSelectedTargetId}
                      platformFilter={scriptDeviceType || null}
                      emptyMessage={`No ${scriptDeviceType || ''} devices found for this client.`}
                    />
                  </div>
                )}

                {targetType === 'multiple' && (
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Select Devices</label>
                    <DevicePicker
                      mode="multiple"
                      devices={compatibleDevices}
                      value={selectedDeviceIds}
                      onChange={setSelectedDeviceIds}
                      platformFilter={scriptDeviceType || null}
                      emptyMessage="No compatible devices available."
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          {hasParams && (
            <div className="bg-purple-50 rounded-lg p-4 border border-purple-100 mb-6">
              <h5 className="text-xs font-bold text-purple-600 uppercase tracking-wide mb-3 flex items-center gap-2">
                <Settings size={12} /> Script Configuration
              </h5>
              <div className="space-y-3">
                {params.map((param) => (
                  <div key={param.name}>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">
                      {param.label} {param.required && <span className="text-red-500">*</span>}
                    </label>
                    {param.type === 'select' ? (
                      <select
                        className="w-full text-sm border-slate-300 rounded-md p-2 focus:ring-purple-500 focus:border-purple-500 bg-white"
                        value={paramValues[param.name] || param.default || ''}
                        onChange={(e) => handleParamChange(param.name, e.target.value)}
                      >
                        {param.options.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </select>
                    ) : param.type === 'checkbox' ? (
                      <div className="flex items-center gap-2 mt-1">
                        <input
                          type="checkbox"
                          id={`param-${param.name}`}
                          className="rounded border-slate-300 text-purple-600 focus:ring-purple-500"
                          checked={paramValues[param.name] !== undefined ? paramValues[param.name] : param.default}
                          onChange={(e) => handleParamChange(param.name, e.target.checked)}
                        />
                        <label htmlFor={`param-${param.name}`} className="text-sm text-slate-700">
                          {param.label}
                        </label>
                      </div>
                    ) : (
                      <input
                        type={param.type}
                        placeholder={param.placeholder}
                        className="w-full text-sm border border-slate-300 rounded-md p-2 focus:ring-purple-500 outline-none"
                        value={paramValues[param.name] || ''}
                        onChange={(e) => handleParamChange(param.name, e.target.value)}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 border border-slate-300 rounded-lg text-slate-600 font-medium hover:bg-slate-50 transition"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={!canSubmit}
              className="flex-1 py-2.5 bg-blue-600 rounded-lg text-white font-medium hover:bg-blue-700 shadow-lg shadow-blue-200 transition flex justify-center items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Deploy Now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeploymentModal;
